import { z } from 'zod';
const LA_POSTE_SIREN = '356000000';
export function normalizeIdentifier(value) {
    return value.replace(/[\s.\-  ]/g, '').toUpperCase();
}
function passesLuhn(digits) {
    const sum = [...digits].reverse().reduce((acc, char, index) => {
        const digit = Number(char);
        if (index % 2 === 0)
            return acc + digit;
        const doubled = digit * 2;
        return acc + (doubled > 9 ? doubled - 9 : doubled);
    }, 0);
    return sum % 10 === 0;
}
export function isValidSiren(value) {
    const siren = normalizeIdentifier(value);
    return /^\d{9}$/.test(siren) && passesLuhn(siren);
}
export function isValidSiret(value) {
    const siret = normalizeIdentifier(value);
    if (!/^\d{14}$/.test(siret))
        return false;
    // La Poste establishments share one SIREN and are numbered with a digit-sum rule instead of Luhn.
    if (siret.startsWith(LA_POSTE_SIREN) && siret !== `${LA_POSTE_SIREN}00048`) {
        return [...siret].reduce((acc, char) => acc + Number(char), 0) % 5 === 0;
    }
    return passesLuhn(siret);
}
export function vatNumberFromSiren(value) {
    const siren = normalizeIdentifier(value);
    if (!isValidSiren(siren))
        return null;
    const key = (12 + 3 * (Number(siren) % 97)) % 97;
    return `FR${String(key).padStart(2, '0')}${siren}`;
}
export function isValidFrenchVatNumber(value) {
    const vat = normalizeIdentifier(value);
    const match = /^FR([0-9A-Z]{2})(\d{9})$/.exec(vat);
    if (!match)
        return false;
    const [, key, siren] = match;
    if (/^\d{2}$/.test(key))
        return vatNumberFromSiren(siren) === vat;
    return isValidSiren(siren);
}
function mod97(numeric) {
    return [...numeric].reduce((remainder, char) => (remainder * 10 + Number(char)) % 97, 0);
}
export function isValidIban(value) {
    const iban = normalizeIdentifier(value);
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban))
        return false;
    const rearranged = `${iban.slice(4)}${iban.slice(0, 4)}`;
    const numeric = rearranged.replace(/[A-Z]/g, (letter) => String(letter.charCodeAt(0) - 55));
    return mod97(numeric) === 1;
}
// IBAN length per SEPA country (ISO 13616 registry): a FR IBAN of 28 characters can pass the
// mod-97 key and still be refused by the bank.
// biome-ignore format: a country table reads better packed
export const EEA_IBAN_LENGTHS = {
    AT: 20, BE: 16, BG: 22, CY: 28, CZ: 24, DE: 22, DK: 18, EE: 20, ES: 24, FI: 18, FR: 27, GR: 27,
    HR: 21, HU: 28, IE: 22, IS: 26, IT: 27, LI: 21, LT: 20, LU: 20, LV: 21, MT: 31, NL: 18, NO: 15,
    PL: 28, PT: 25, RO: 24, SE: 24, SI: 19, SK: 24,
};
// SEPA members outside the EEA are reachable too, but only with the creditor bank's BIC.
// biome-ignore format: a country table reads better packed
export const IBAN_LENGTHS = {
    ...EEA_IBAN_LENGTHS,
    AD: 24, AL: 28, CH: 21, GB: 22, GI: 23, MC: 27, MD: 24, ME: 22, MK: 19, SM: 27, VA: 22,
};
// The pain.001 schema's own BIC pattern: 8 or 11 characters, location code never « O » second.
export function isValidBic(value) {
    return /^[A-Z]{6}[A-Z2-9][A-NP-Z0-9]([A-Z0-9]{3})?$/.test(normalizeIdentifier(value));
}
// Checked to the key: these go into the SEPA transfer file, which the bank refuses whole on a
// single bad one. Blank passes.
export const ibanField = z
    .string()
    .nullable()
    .refine((value) => !value?.trim() || isValidIban(value), {
    message: "L'IBAN est invalide (format ou clé de contrôle incorrects)",
});
export const bicField = z
    .string()
    .nullable()
    .refine((value) => !value?.trim() || isValidBic(value), {
    message: 'Le BIC doit contenir 8 ou 11 caractères (ex : BNPAFRPPXXX)',
});
